/**
 * GeneralTab - first sub-tab of the action modal.
 *
 * Covers identity + routing fields: key, label, icon, applicability,
 * actor, trigger, source_state (when applicability=STATE),
 * target_state, order, priority.
 *
 * Inputs bind directly to the single parent `useForm` via
 * `formCtx.values.form.*` / `formCtx.setFieldValue('form.*', next)`.
 */

import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import type {
  WorkflowActor,
  WorkflowApplicability,
  WorkflowStateAdmin,
  WorkflowTrigger,
} from '@/admin/hooks/use-workflow-definitions'

import { RequiredAttrsPicker } from './required-attrs-picker'
import {
  readForm,
  setFormField,
  type ActionFormContext,
} from './types'

const APPLICABILITY_OPTIONS: { value: WorkflowApplicability; label: string }[] =
  [
    { value: 'CREATION', label: 'Creation - on new resource' },
    { value: 'STATE', label: 'State - from a source state' },
    { value: 'GLOBAL', label: 'Global - any state' },
  ]

const ACTOR_OPTIONS: { value: WorkflowActor; label: string }[] = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'CONSUMER', label: 'Consumer' },
  { value: 'SYSTEM', label: 'System' },
]

const TRIGGER_OPTIONS: { value: WorkflowTrigger; label: string }[] = [
  { value: 'USER', label: 'User' },
  { value: 'TIMER', label: 'Timer' },
  { value: 'WEBHOOK', label: 'Webhook' },
  { value: 'SYSTEM', label: 'System' },
  { value: 'EVENT', label: 'Event' },
]

interface GeneralTabProps {
  formCtx: ActionFormContext
  states: WorkflowStateAdmin[]
  mode: 'create' | 'edit'
  workflowUuid: string
}

export function GeneralTab({
  formCtx,
  states,
  mode,
  workflowUuid,
}: GeneralTabProps) {
  const form = readForm(formCtx)

  const sortedStates = [...states].sort((a, b) => a.order - b.order)
  const stateOptions = sortedStates.map((s) => ({
    value: s.uuid,
    label: s.label,
  }))

  function setKey(next: string) {
    setFormField(formCtx, 'key', next)
  }
  function setLabel(next: string) {
    if (mode === 'create' && looksDerived(form.key, form.label)) {
      setFormField(formCtx, 'key', toKey(next))
    }
    setFormField(formCtx, 'label', next)
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <Cell span={1}>
        <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          Label
        </Label>
        <Input
          value={form.label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Approve"
          data-testid="workflow-action-label"
        />
      </Cell>
      <Cell span={1}>
        <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          Key
        </Label>
        <Input
          value={form.key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="approve"
          disabled={mode === 'edit'}
          data-testid="workflow-action-key"
        />
      </Cell>

      <Cell span={1}>
        <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          Icon (lucide name, optional)
        </Label>
        <Input
          value={form.icon ?? ''}
          onChange={(e) => setFormField(formCtx, 'icon', e.target.value)}
          placeholder="check"
        />
      </Cell>
      <Cell span={1}>
        <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          Applicability
        </Label>
        <Select
          value={form.applicability ?? 'STATE'}
          onValueChange={(next: string) =>
            setFormField(formCtx, 'applicability', next as WorkflowApplicability)
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {APPLICABILITY_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Cell>

      <Cell span={1}>
        <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          Actor
        </Label>
        <Select
          value={form.actor ?? 'ADMIN'}
          onValueChange={(next: string) =>
            setFormField(formCtx, 'actor', next as WorkflowActor)
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ACTOR_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Cell>
      <Cell span={1}>
        <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          Trigger
        </Label>
        <Select
          value={form.trigger ?? 'USER'}
          onValueChange={(next: string) =>
            setFormField(formCtx, 'trigger', next as WorkflowTrigger)
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TRIGGER_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Cell>

      {form.applicability === 'STATE' && (
        <Cell span={1}>
          <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
            Source state
          </Label>
          <Select
            value={form.source_state ?? ''}
            onValueChange={(next: string) =>
              setFormField(formCtx, 'source_state', next || null)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a source state" />
            </SelectTrigger>
            <SelectContent>
              {stateOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Cell>
      )}

      <Cell span={1}>
        <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          Target state (optional)
        </Label>
        <Select
          value={form.target_state ?? '__none__'}
          onValueChange={(next: string) =>
            setFormField(
              formCtx,
              'target_state',
              next === '__none__' ? null : next,
            )
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">- None -</SelectItem>
            {stateOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Cell>

      <Cell span={1}>
        <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          Order
        </Label>
        <Input
          type="number"
          value={form.order ?? 0}
          onChange={(e) =>
            setFormField(formCtx, 'order', Number(e.target.value) || 0)
          }
        />
      </Cell>
      <Cell span={1}>
        <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          Priority
        </Label>
        <Input
          type="number"
          value={form.priority ?? 0}
          onChange={(e) =>
            setFormField(formCtx, 'priority', Number(e.target.value) || 0)
          }
        />
      </Cell>

      <Cell span={2}>
        <RequiredAttrsPicker formCtx={formCtx} workflowUuid={workflowUuid} />
      </Cell>
    </div>
  )
}

function Cell({ children, span }: { children: React.ReactNode; span: 1 | 2 }) {
  return (
    <div
      className={`flex flex-col gap-1 ${span === 2 ? 'col-span-2' : 'col-span-1'}`}
    >
      {children}
    </div>
  )
}

function toKey(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function looksDerived(currentKey: string, currentLabel: string): boolean {
  if (!currentKey) return true
  return currentKey === toKey(currentLabel)
}
